
import React, { useState, useEffect } from "react";
import { useToast } from "@/hooks/use-toast";
import { Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Pencil, Plus, Save, Trash2, Copy, X } from "lucide-react";
import { db } from "@/integrations/cloudflare/client";

interface CraftIdea {
  id: string;
  title: string;
  instructions: string;
  required_supplies: string[];
  difficulty: string;
  time_required: string | null;
}

const CraftIdeasManager: React.FC<{ selectedSupplies: string[] }> = ({ selectedSupplies }) => {
  const { toast } = useToast();
  const [craftIdeas, setCraftIdeas] = useState<CraftIdea[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedCraft, setSelectedCraft] = useState<CraftIdea | null>(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [formData, setFormData] = useState<Omit<CraftIdea, 'id'>>({
    title: '',
    instructions: '',
    required_supplies: [],
    difficulty: 'Medium',
    time_required: ''
  });
  const [newSupply, setNewSupply] = useState('');

  // Load craft ideas from the database
  useEffect(() => {
    const fetchCraftIdeas = async () => {
      try {
        const { data, error } = await db
          .from('craft_ideas')
          .select('*')
          .order('title');
          
        if (error) throw error;
        setCraftIdeas(data || []);
      } catch (error) {
        console.error('Error fetching craft ideas:', error);
        toast({
          title: 'Failed to load craft ideas',
          description: 'Please refresh the page and try again.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchCraftIdeas();
  }, [toast]);

  const handleAddCraft = () => {
    setSelectedCraft(null);
    setFormData({
      title: '',
      instructions: '',
      required_supplies: selectedSupplies.filter(s => s.trim() !== ''),
      difficulty: 'Medium',
      time_required: ''
    });
    setIsDialogOpen(true);
  };

  const handleEditCraft = (craft: CraftIdea) => {
    setSelectedCraft(craft);
    setFormData({
      title: craft.title,
      instructions: craft.instructions,
      required_supplies: craft.required_supplies,
      difficulty: craft.difficulty,
      time_required: craft.time_required || ''
    });
    setIsDialogOpen(true);
  };

  const handleAddSupply = () => {
    if (newSupply.trim() === '') return;
    if (!formData.required_supplies.includes(newSupply.trim())) {
      setFormData({
        ...formData,
        required_supplies: [...formData.required_supplies, newSupply.trim()]
      });
    }
    setNewSupply('');
  };

  const handleRemoveSupply = (supply: string) => {
    setFormData({
      ...formData,
      required_supplies: formData.required_supplies.filter(s => s !== supply)
    });
  };

  const handleSaveCraft = async () => {
    try {
      if (formData.title.trim() === '') {
        toast({
          title: 'Title is required',
          description: 'Please enter a title for the craft idea.',
          variant: 'destructive',
        });
        return;
      }

      if (formData.instructions.trim() === '') {
        toast({
          title: 'Instructions are required',
          description: 'Please enter instructions for the craft idea.',
          variant: 'destructive',
        });
        return;
      }

      if (formData.required_supplies.length === 0) {
        toast({
          title: 'Supplies are required',
          description: 'Please add at least one required supply for the craft idea.',
          variant: 'destructive',
        });
        return;
      }

      if (selectedCraft) {
        // Update existing craft
        const { error } = await db
          .from('craft_ideas')
          .update({
            title: formData.title.trim(),
            instructions: formData.instructions.trim(),
            required_supplies: formData.required_supplies,
            difficulty: formData.difficulty,
            time_required: formData.time_required || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedCraft.id);

        if (error) throw error;
        
        setCraftIdeas(craftIdeas.map(craft => 
          craft.id === selectedCraft.id 
            ? { 
                ...craft, 
                title: formData.title.trim(),
                instructions: formData.instructions.trim(),
                required_supplies: formData.required_supplies,
                difficulty: formData.difficulty,
                time_required: formData.time_required || null 
              } 
            : craft
        ));

        toast({
          title: 'Craft updated',
          description: `"${formData.title}" has been updated successfully.`,
        });
      } else {
        // Create new craft
        const { data, error } = await db
          .from('craft_ideas')
          .insert({
            title: formData.title.trim(),
            instructions: formData.instructions.trim(),
            required_supplies: formData.required_supplies,
            difficulty: formData.difficulty,
            time_required: formData.time_required || null
          })
          .select();

        if (error) throw error;
        
        if (data && data.length > 0) {
          setCraftIdeas([...craftIdeas, data[0]]);
        }

        toast({
          title: 'Craft created',
          description: `"${formData.title}" has been created successfully.`,
        });
      }

      setIsDialogOpen(false);
    } catch (error) {
      console.error('Error saving craft:', error);
      toast({
        title: 'Failed to save craft',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  const handleDeleteCraft = async (craftId: string) => {
    if (!confirm('Are you sure you want to delete this craft idea?')) return;

    try {
      const { error } = await db
        .from('craft_ideas')
        .delete()
        .eq('id', craftId);

      if (error) throw error;
      
      setCraftIdeas(craftIdeas.filter(craft => craft.id !== craftId));

      toast({
        title: 'Craft deleted',
        description: 'The craft idea has been deleted successfully.',
      });
    } catch (error) {
      console.error('Error deleting craft:', error);
      toast({
        title: 'Failed to delete craft',
        description: 'Please try again later.',
        variant: 'destructive',
      });
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex justify-between items-center">
          <span>Manage Craft Ideas</span>
          <Button onClick={handleAddCraft} className="bg-elfGreen hover:bg-elfGreen-dark">
            <Plus className="h-4 w-4 mr-2" /> Add New Craft
          </Button>
        </CardTitle>
        <CardDescription>
          Create and manage curated craft ideas that will be shown in the generator
        </CardDescription>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="text-center py-4">Loading craft ideas...</div>
        ) : craftIdeas.length === 0 ? (
          <div className="text-center py-10 border rounded-lg bg-gray-50">
            <p className="text-gray-500 mb-4">No craft ideas created yet</p>
            <Button onClick={handleAddCraft} className="bg-elfGreen hover:bg-elfGreen-dark">
              <Plus className="h-4 w-4 mr-2" /> Create Your First Craft Idea
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            {craftIdeas.map(craft => (
              <div key={craft.id} className="border rounded-lg p-4">
                <div className="flex justify-between items-start">
                  <div>
                    <h3 className="font-medium">{craft.title}</h3>
                    <p className="text-sm text-gray-600 mb-2">
                      <span className="font-medium">Difficulty:</span> {craft.difficulty}
                      {craft.time_required && (
                        <span className="ml-3">
                          <span className="font-medium">Time:</span> {craft.time_required}
                        </span>
                      )}
                    </p>
                    <div className="flex flex-wrap gap-1 mb-2">
                      {craft.required_supplies.map((supply, index) => (
                        <span 
                          key={index}
                          className="bg-elfGreen-lighter text-elfGreen-dark text-xs px-2 py-1 rounded-full"
                        >
                          {supply}
                        </span>
                      ))}
                    </div>
                    <p className="text-sm text-gray-700 line-clamp-2">
                      {craft.instructions}
                    </p>
                  </div>
                  <div className="flex space-x-2">
                    <Button 
                      variant="outline" 
                      size="sm" 
                      onClick={() => handleEditCraft(craft)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button 
                      variant="outline" 
                      size="sm"
                      className="text-red-500 hover:text-red-700"
                      onClick={() => handleDeleteCraft(craft.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </CardContent>

      <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle>{selectedCraft ? 'Edit Craft Idea' : 'Add New Craft Idea'}</DialogTitle>
            <DialogDescription>
              {selectedCraft 
                ? 'Update the details of this craft idea' 
                : 'Create a new craft idea for the generator'}
            </DialogDescription>
          </DialogHeader>

          <div className="grid gap-4 py-4">
            <div className="grid grid-cols-1 gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                placeholder="Enter craft title"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="difficulty">Difficulty Level</Label>
                <Select
                  value={formData.difficulty}
                  onValueChange={(value) => setFormData({ ...formData, difficulty: value })}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select difficulty" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Easy">Easy</SelectItem>
                    <SelectItem value="Medium">Medium</SelectItem>
                    <SelectItem value="Hard">Hard</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="time">Time Required (optional)</Label>
                <Input
                  id="time"
                  value={formData.time_required || ''}
                  onChange={(e) => setFormData({ ...formData, time_required: e.target.value })}
                  placeholder="e.g. 30 minutes"
                />
              </div>
            </div>

            <div>
              <Label>Required Supplies</Label>
              <div className="flex flex-wrap gap-2 mt-1 mb-2">
                {formData.required_supplies.map((supply, index) => (
                  <div 
                    key={index}
                    className="bg-elfGreen-lighter text-elfGreen-dark text-sm px-2 py-1 rounded-full flex items-center"
                  >
                    {supply}
                    <button
                      type="button"
                      onClick={() => handleRemoveSupply(supply)}
                      className="ml-1 text-elfGreen-dark hover:text-red-500"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </div>
                ))}
              </div>
              <div className="flex gap-2">
                <Input
                  value={newSupply}
                  onChange={(e) => setNewSupply(e.target.value)}
                  placeholder="Add supply"
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddSupply();
                    }
                  }}
                />
                <Button 
                  type="button" 
                  onClick={handleAddSupply}
                  disabled={!newSupply.trim()}
                  className="bg-elfGreen hover:bg-elfGreen-dark"
                >
                  Add
                </Button>
              </div>
            </div>

            <div>
              <Label htmlFor="instructions">Instructions</Label>
              <Textarea
                id="instructions"
                value={formData.instructions}
                onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
                placeholder="Enter step-by-step instructions for the craft"
                className="min-h-[150px]"
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSaveCraft} className="bg-elfGreen hover:bg-elfGreen-dark">
              <Save className="h-4 w-4 mr-2" />
              {selectedCraft ? 'Update Craft' : 'Create Craft'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
};

export default CraftIdeasManager;
